import { useTranslation } from 'react-i18next';

import { normalizeStatus } from './index';

interface StatusToggleButtonProps {
  status: string;
  isLoading: boolean;
  hasPermission: boolean;
  onToggle: () => void;
}

// ── Template: AvailableEnabledButtonTemplate ──
// 2-col toggle: gray "Unavailable" (inactive) | GREEN "Available" (active)
function AvailableToggle({ onToggle, isLoading }: { onToggle: () => void; isLoading: boolean }) {
  const { t } = useTranslation();
  return (
    <button
      type="button"
      onClick={onToggle}
      disabled={isLoading}
      className="flex w-full overflow-hidden rounded-lg"
    >
      {/* Inactive side — Unavailable (gray) */}
      <div className="flex-1 flex items-center justify-center bg-[#D9D9D9] py-2.5 md:py-3.5">
        <span className="text-[10px] sm:text-xs font-poppins text-white truncate px-1">
          {t('dockDoors.unavailable')}
        </span>
      </div>
      {/* Active side — Available (green) */}
      <div className="flex-1 flex items-center justify-center bg-[#2FA851] py-2.5 md:py-3.5">
        <span className="text-[10px] sm:text-xs font-poppins text-white truncate px-1">
          {t('dockDoors.available')}
        </span>
      </div>
    </button>
  );
}

// ── Template: UnavailableEnabledButtonTemplate ──
// 2-col toggle: RED "Unavailable" (active) | gray "Available" (inactive)
function UnavailableToggle({ onToggle, isLoading }: { onToggle: () => void; isLoading: boolean }) {
  const { t } = useTranslation();
  return (
    <button
      type="button"
      onClick={onToggle}
      disabled={isLoading}
      className="flex w-full overflow-hidden rounded-lg"
    >
      {/* Active side — Unavailable (red) */}
      <div className="flex-1 flex items-center justify-center bg-[#FF2323] py-2.5 md:py-3.5">
        <span className="text-[10px] sm:text-xs font-poppins text-white truncate px-1">
          {t('dockDoors.unavailable')}
        </span>
      </div>
      {/* Inactive side — Available (gray) */}
      <div className="flex-1 flex items-center justify-center bg-[#D9D9D9] py-2.5 md:py-3.5">
        <span className="text-[10px] sm:text-xs font-poppins text-white truncate px-1">
          {t('dockDoors.available')}
        </span>
      </div>
    </button>
  );
}

// ── Template: InUseUneditableTemplate ──
// Blue "Status cannot be changed" on #BDD1E3 bg
function InUseUneditable() {
  const { t } = useTranslation();
  return (
    <div className="flex w-full items-center justify-center rounded-lg bg-[#BDD1E3] py-2.5 md:py-3.5 px-2">
      <span className="text-[10px] sm:text-xs font-poppins text-[#1357A0] text-center">
        {t('dockDoors.statusCannotBeChanged')}
      </span>
    </div>
  );
}

// ── Template: PendingInUseUneditableTemplate ──
// Orange "Status cannot be changed" on #FFEDC0 bg
function PendingInUseUneditable() {
  const { t } = useTranslation();
  return (
    <div className="flex w-full items-center justify-center rounded-lg bg-[#FFEDC0] py-2.5 md:py-3.5 px-2">
      <span className="text-[10px] sm:text-xs font-poppins text-[#E38C25] text-center">
        {t('dockDoors.statusCannotBeChanged')}
      </span>
    </div>
  );
}

// ── Template: DockDoorStatusChangeTemplate ──
// Red info text "Door can be enable or disable in Manhattan WMS" on #BDD1E3 bg
function DockDoorStatusChangeInfo() {
  const { t } = useTranslation();
  return (
    <div className="flex w-full items-center justify-center rounded-lg bg-[#BDD1E3] py-2.5 md:py-3.5 px-2">
      <span className="text-[10px] sm:text-xs font-poppins text-[#FF2323] text-center">
        {t('dockDoors.doorCanBeToggled')}
      </span>
    </div>
  );
}

export function StatusToggleButton({
  status,
  isLoading,
  hasPermission,
  onToggle,
}: StatusToggleButtonProps) {
  const normalized = normalizeStatus(status);

  // ── ACL = false (no permission) ──
  if (!hasPermission) {
    if (normalized === 'In Use (Full)') {
      return <InUseUneditable />;
    }
    // Available, Unavailable, Pending In Use → red info message
    return <DockDoorStatusChangeInfo />;
  }

  // ── ACL = true (has permission) ──
  switch (normalized) {
    case 'Available':
      return <AvailableToggle onToggle={onToggle} isLoading={isLoading} />;
    case 'Unavailable':
      return <UnavailableToggle onToggle={onToggle} isLoading={isLoading} />;
    case 'In Use (Full)':
      return <InUseUneditable />;
    case 'Pending In Use':
      return <PendingInUseUneditable />;
    default:
      return <DockDoorStatusChangeInfo />;
  }
}
